import { useCallback, useEffect } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useLanguage } from '../../locale/useLanguage';
import {
  CmsConfigs,
  // PremiseType,
  WeekDay,
} from '../../models';
import { validateConciergeHours } from '../../views/OpeningHours/openingHoursHelper';
import { useOrder } from '../useOrder';
import dayjs from 'dayjs';
import { getTotalHours } from '../../views/OpeningHours/openingHoursHelper';

export type OpeningHoursForm = {
  // premiseType: PremiseType;
  days: WeekDay[];
  conciergeMorningFromHour: string;
  conciergeMorningToHour: string;
  conciergeAfternoonFromHour: string;
  conciergeAfternoonToHour: string;
};

export const useOpeningHoursForm = (
  cmsConfigs?: Pick<
    CmsConfigs,
    'receptionMinimumHoursPerDay' | 'receptionMinimumDaysPerWeek'
  >,
  conciergeInfo?: OpeningHoursForm
) => {
  const [t] = useLanguage();

  const {
    handleSubmit,
    setValue: setFormValue,
    control,
    getValues: getFormValues,
    formState: { errors },
    trigger,
  } = useForm<OpeningHoursForm>({ defaultValues: conciergeInfo });

  const order = useOrder();

  useEffect(() => {
    if (conciergeInfo) {
      setFormValue(
        'conciergeAfternoonFromHour',
        conciergeInfo.conciergeAfternoonFromHour
      );
      setFormValue(
        'conciergeAfternoonToHour',
        conciergeInfo.conciergeAfternoonToHour
      );
      setFormValue(
        'conciergeMorningFromHour',
        conciergeInfo.conciergeMorningFromHour
      );
      setFormValue(
        'conciergeMorningToHour',
        conciergeInfo.conciergeMorningToHour
      );
      setFormValue('days', conciergeInfo.days);
      // setFormValue('premiseType', conciergeInfo.premiseType);
    }
  }, [conciergeInfo, setFormValue]);

  const validateReceptionHours = useCallback(() => {
    trigger('days');
    let minimumHours = cmsConfigs?.receptionMinimumHoursPerDay;
    if (order?.receptionRules?.minRangeWidthPerDay) {
      minimumHours = dayjs
        .duration(order?.receptionRules?.minRangeWidthPerDay)
        .asHours();
    }
    const {
      conciergeMorningFromHour,
      conciergeMorningToHour,
      conciergeAfternoonFromHour,
      conciergeAfternoonToHour,
    } = getFormValues();

    return validateConciergeHours({
      receptionAfternoonFromHour: conciergeAfternoonFromHour,
      receptionAfternoonToHour: conciergeAfternoonToHour,
      receptionMorningFromHour: conciergeMorningFromHour,
      receptionMorningToHour: conciergeMorningToHour,
      t,
      minimumHours,
    });
  }, [
    cmsConfigs?.receptionMinimumHoursPerDay,
    getFormValues,
    t,
    order,
    trigger,
  ]);

  // const { field: premiseTypeField } = useController<
  //   OpeningHoursForm,
  //   'premiseType'
  // >({
  //   control,
  //   name: 'premiseType',
  //   defaultValue: PremiseType.Reception,
  // });

  // const onPremiseTypeChange = useCallback(
  //   (val: PremiseType) => {
  //     setFormValue('premiseType', val, { shouldValidate: true });
  //   },
  //   [setFormValue]
  // );

  const { field: weekDaysField } = useController<OpeningHoursForm, 'days'>({
    control,
    name: 'days',
    defaultValue: [],
    rules: {
      validate: (weekDays) => {
        const minimumDays = cmsConfigs?.receptionMinimumDaysPerWeek;
        const minimumHoursInWeek = order?.receptionRules?.minRangesWidthPerWeek
          ? dayjs
              .duration(order?.receptionRules?.minRangesWidthPerWeek)
              .asHours()
          : null;

        if (minimumHoursInWeek) {
          const {
            conciergeMorningFromHour,
            conciergeMorningToHour,
            conciergeAfternoonFromHour,
            conciergeAfternoonToHour,
          } = getFormValues();
          const dailyHours = getTotalHours(
            conciergeMorningFromHour,
            conciergeMorningToHour,
            conciergeAfternoonFromHour,
            conciergeAfternoonToHour
          );
          if (!weekDays)
            return t('addressNotesView.receptionWeekHoursError', {
              hours: minimumHoursInWeek,
            });
          if (dailyHours * weekDays!.length < minimumHoursInWeek) {
            return t('addressNotesView.receptionWeekHoursError', {
              hours: minimumHoursInWeek,
            });
          }
        } else {
          if (!weekDays)
            return t('addressNotesView.receptionDaysError', {
              days: minimumDays,
            });

          if (minimumDays && weekDays!.length < minimumDays)
            return t('addressNotesView.receptionDaysError', {
              days: minimumDays,
            });
        }

        return true;
      },
    },
  });

  const onWeekDaysChange = useCallback(
    (val: WeekDay) => {
      const indexVal = weekDaysField.value!.findIndex((wd) => wd === val);
      const weekDayAlreadyExist = indexVal >= 0;
      let updatedWeekDays: WeekDay[];
      if (weekDayAlreadyExist) {
        updatedWeekDays = [...weekDaysField.value!];
        updatedWeekDays.splice(indexVal, 1);
      } else {
        updatedWeekDays = [...weekDaysField.value!, val];
      }
      setFormValue('days', updatedWeekDays, { shouldValidate: true });
    },
    [setFormValue, weekDaysField.value]
  );

  const { field: receptionMorningFromHourField } = useController<
    OpeningHoursForm,
    'conciergeMorningFromHour'
  >({
    control,
    name: 'conciergeMorningFromHour',
    defaultValue: '08',
    rules: {
      validate: () => validateReceptionHours(),
    },
  });

  const onReceptionMorningFromHourChange = useCallback(
    (val: string | undefined) => {
      setFormValue('conciergeMorningFromHour', val!, {
        shouldValidate: true,
      });
    },
    [setFormValue]
  );

  const { field: receptionMorningToHourField } = useController<
    OpeningHoursForm,
    'conciergeMorningToHour'
  >({
    control,
    name: 'conciergeMorningToHour',
    defaultValue: '13',
    rules: {
      validate: () => validateReceptionHours(),
    },
  });

  const onReceptionMorningToHourChange = useCallback(
    (val: string | undefined) => {
      setFormValue('conciergeMorningToHour', val!, {
        shouldValidate: true,
      });
    },
    [setFormValue]
  );

  const { field: receptionAfternoonFromHourField } = useController<
    OpeningHoursForm,
    'conciergeAfternoonFromHour'
  >({
    control,
    name: 'conciergeAfternoonFromHour',
    defaultValue: '14',
    rules: {
      validate: () => validateReceptionHours(),
    },
  });

  const onReceptionAfternoonFromHourChange = useCallback(
    (val: string | undefined) => {
      setFormValue('conciergeAfternoonFromHour', val!, {
        shouldValidate: true,
      });
    },
    [setFormValue]
  );

  const { field: receptionAfternoonToHourField } = useController<
    OpeningHoursForm,
    'conciergeAfternoonToHour'
  >({
    control,
    name: 'conciergeAfternoonToHour',
    defaultValue: '18',
    rules: {
      validate: () => validateReceptionHours(),
    },
  });

  const onReceptionAfternoonToHourChange = useCallback(
    (val: string | undefined) => {
      setFormValue('conciergeAfternoonToHour', val!, {
        shouldValidate: true,
      });
    },
    [setFormValue]
  );

  return {
    // premiseTypeField,
    // onPremiseTypeChange,
    weekDaysField,
    onWeekDaysChange,
    receptionAfternoonFromHourField,
    onReceptionAfternoonFromHourChange,
    receptionAfternoonToHourField,
    onReceptionAfternoonToHourChange,
    receptionMorningFromHourField,
    onReceptionMorningFromHourChange,
    receptionMorningToHourField,
    onReceptionMorningToHourChange,
    handleSubmit,
    errors,
  };
};
